import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api, dataBr, dataHoraBr, diaNaCasa, enderecoDoDocumento, ErroDoPortal, tamanho,
} from '../api';
import type { OsResumo } from './Acervo';
import { NomeDaAmostra, nomeDaAmostra } from '../NomeDaAmostra';

interface Filtro {
  eixo: 'amostra' | 'ensaio';
  chave: string;
  rotulo: string;
  documentos: string;
  /** Eixo amostra: o nome que o cliente deu (onda 303, RN-248); o `rotulo` é o código. */
  identificacao?: string | null;
}

interface Documento {
  tipo: 'relatorio' | 'recebimento';
  documento_id: string;
  numero: string;
  revisao: number;
  titulo: string;
  emitido_em: string | null;
  publicado_em: string | null;
  token_autenticidade: string | null;
  nome_arquivo: string;
  tamanho_bytes: string;
  amostras_codigo: string[];
  /** O nome do cliente de cada amostra, na ordem de `amostras_codigo` (onda 303). */
  amostras_identificacao?: (string | null)[];
  metodos_nome: string[];
}

interface CelulaDoQuadro {
  metodo_codigo: string;
  metodo_nome: string;
  granularidade: string;
  amostra_id: string;
  amostra_codigo: string;
  amostra_identificacao: string | null;
  ensaios: string;
  corpos_de_prova: string;
  com_laudo: string;
  ultima_aprovacao: string | null;
}

interface Ficha {
  os: OsResumo & {
    endereco: string | null;
    data_prevista_inicio: string | null;
    data_realizada_fim: string | null;
  };
  filtros: { amostras: Filtro[]; ensaios: Filtro[] };
  documentos: Documento[];
  quadro: CelulaDoQuadro[];
}

/** Abaixo disto, o visualizador embutido de PDF não se comporta. */
const LARGURA_DO_VISOR = 900;

function useLargo(): boolean {
  const [largo, setLargo] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia(`(min-width: ${LARGURA_DO_VISOR}px)`).matches);
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${LARGURA_DO_VISOR}px)`);
    const ouvir = () => setLargo(mq.matches);
    mq.addEventListener('change', ouvir);
    return () => mq.removeEventListener('change', ouvir);
  }, []);
  return largo;
}

/* O dia da entrega em Brasília, não em UTC (UI-T-01, onda 329). */
const soODia = (v: string | null) => diaNaCasa(v) || 'sem-data';

/**
 * Uma OS: o contrato, o que foi executado, e os documentos.
 *
 * ── Ver na tela ou baixar ────────────────────────────────────────────
 *
 * As duas coisas, e a escolha é do cliente — porque para o servidor dá no
 * mesmo. Exibir e baixar movem exatamente os mesmos bytes; quem desenha
 * as páginas é o navegador de quem está lendo. O que custaria caro seria
 * GERAR o PDF a cada abertura, e isso não acontece: o documento foi
 * gerado uma vez, na emissão, e está guardado.
 *
 * ── Onde o visor aparece, e onde não ─────────────────────────────────
 *
 * Acima de 900 px, o PDF abre embutido na página, sem sair da lista.
 * Abaixo disso, não: no celular o visualizador embutido em quadro varia
 * de comportamento entre iOS e Android e às vezes só mostra a primeira
 * página, sem rolagem e sem zoom. Ali o botão abre em aba nova, que
 * entrega o leitor nativo do aparelho.
 *
 * O botão «Baixar» existe nos dois casos, sempre.
 */
export function OrdemDeServico() {
  const { id = '' } = useParams();
  const [ficha, setFicha] = useState<Ficha | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [amostra, setAmostra] = useState<string | null>(null);
  const [ensaio, setEnsaio] = useState<string | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);
  const largo = useLargo();

  const carregar = useCallback((sinal: AbortSignal) => {
    const q = new URLSearchParams();
    if (amostra) q.set('amostra', amostra);
    if (ensaio) q.set('ensaio', ensaio);
    api<Ficha>(`/os/${id}?${q}`, { sinal })
      .then((f) => { setFicha(f); setErro(null); })
      .catch((e) => {
        if ((e as Error)?.name === 'AbortError') return;
        setErro(e instanceof ErroDoPortal ? e.message : 'Não foi possível carregar.');
      });
  }, [id, amostra, ensaio]);

  useEffect(() => {
    const corte = new AbortController();
    carregar(corte.signal);
    return () => corte.abort();
  }, [carregar]);

  /* Os documentos, agrupados pelo dia em que foram entregues. */
  const lotes = useMemo(() => {
    const mapa = new Map<string, Documento[]>();
    for (const d of ficha?.documentos ?? []) {
      const dia = soODia(d.publicado_em);
      const lista = mapa.get(dia);
      if (lista) lista.push(d); else mapa.set(dia, [d]);
    }
    return [...mapa.entries()];
  }, [ficha]);

  if (erro) {
    return (
      <div>
        <p><Link className="voltar" to="/">← Voltar</Link></p>
        <div className="aviso ruim" role="alert">{erro}</div>
      </div>
    );
  }
  if (!ficha) return <p className="sub">Carregando…</p>;

  const { os, filtros, documentos, quadro } = ficha;
  const filtrando = Boolean(amostra || ensaio);

  return (
    <div>
      <Link className="voltar" to="/">← Todas as ordens de serviço</Link>

      <h1>{os.obra_nome}</h1>
      <p className="linha-obra">
        <span><b>Ordem de serviço</b> <span className="mono">{os.numero}</span></span>
        {os.municipio && <span><b>Local</b> {os.municipio}/{os.uf ?? ''}</span>}
      </p>

      {/*
        * O bloco de identificação, com rótulo à direita e valor à
        * esquerda — a mesma tabela que abre todo documento do SGQ.
        */}
      <div className="secao"><h2>Contrato</h2></div>
      <dl className="identificacao">
        <dt>Ordem de serviço</dt><dd className="mono">{os.numero}</dd>
        <dt>Obra</dt><dd>{os.obra_nome}</dd>
        <dt>Proposta</dt>
        <dd className={os.proposta_numero ? 'mono' : ''}>
          {os.proposta_numero ?? <span className="nada">não informada</span>}
        </dd>
        <dt>Endereço</dt>
        <dd>{os.endereco ?? <span className="nada">não informado</span>}</dd>
        <dt>Prazo contratual</dt><dd className="mono">{dataBr(os.prazo_contratual)}</dd>
        <dt>Início dos serviços</dt><dd className="mono">{dataBr(os.data_prevista_inicio)}</dd>
        <dt>Situação</dt><dd>{os.situacao}</dd>
      </dl>

      <QuadroDeEnsaios celulas={quadro} />

      <RelatoriosInterativos osId={id} />

      <div className="secao">
        <h2>Documentos</h2>
        <span className="conta">{os.documentos} entregue(s)</span>
      </div>

      <Eixo titulo="Filtrar por amostra" opcoes={filtros.amostras}
            atual={amostra} aoEscolher={setAmostra} amostras />
      <Eixo titulo="Filtrar por tipo de ensaio" opcoes={filtros.ensaios}
            atual={ensaio} aoEscolher={setEnsaio} />

      {filtrando && (
        <p className="sub" style={{ marginTop: '.8rem' }}>
          {documentos.length} de {os.documentos} documento(s) ·{' '}
          <button type="button" className="limpar"
                  onClick={() => { setAmostra(null); setEnsaio(null); }}>
            limpar filtros
          </button>
        </p>
      )}

      {documentos.length === 0 ? (
        <p className="nada" style={{ marginTop: '1.2rem' }}>
          Nenhum documento com esse filtro. Limpe os filtros para ver todos.
        </p>
      ) : (
        /*
          * A régua de entregas.
          *
          * Os documentos pendem de marcas datadas, agrupados pelo lote em
          * que a Testesolo entregou. É a pergunta que o cliente traz — «o
          * que chegou desde a última vez que olhei» —, e é como este
          * público lê um boletim: de cima para baixo, registrado contra
          * uma escala marcada.
          */
        <ol className="registro">
          {lotes.map(([dia, docs]) => (
            <li className="lote" key={dia}>
              <div className="lote-data">
                <time dateTime={dia === 'sem-data' ? undefined : dia}>
                  {dia === 'sem-data' ? '—' : dataBr(dia)}
                </time>
                <span className="quantos">
                  {docs.length} documento{docs.length > 1 ? 's' : ''}
                </span>
              </div>
              <ul className="docs">
                {docs.map((d) => (
                  <CartaoDoDocumento
                    key={`${d.tipo}:${d.documento_id}`}
                    d={d}
                    largo={largo}
                    aberto={aberto === d.documento_id}
                    aoAbrir={() => setAberto(
                      aberto === d.documento_id ? null : d.documento_id)}
                  />
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * O quadro de ensaios executados (`0174`).
 *
 * ── Para que ele existe ──────────────────────────────────────────────
 *
 * Para o cliente conferir o boletim de medição. O boletim diz «12
 * ensaios de teor de umidade»; conferir isso exige ver QUAIS ensaios, em
 * QUAIS amostras — um número solto no portal só repetiria o número do
 * boletim.
 *
 * Por isso é uma matriz: método nas linhas, amostra nas colunas,
 * contagem na célula, total na ponta. É o formato que a casa já usa
 * internamente, e é o que deixa somar com o dedo.
 *
 * ── O que ele conta, e o que não mostra ──────────────────────────────
 *
 * Conta ensaio APROVADO — o mesmo recorte da medição, e é por isso que
 * os dois fecham. A célula de um ensaio cujo laudo ainda não foi
 * entregue vem sublinhada, e a legenda escreve o que isso quer dizer.
 *
 * Não há resultado nenhum aqui: nem umidade, nem LL, nem índice de
 * vazios. O quadro responde «foi feito» e «quantos», nunca «deu quanto».
 * E não há número comercial — previsto, saldo e percentual passam pela
 * conferência do boletim antes de chegar ao cliente.
 */
function QuadroDeEnsaios({ celulas }: { celulas: CelulaDoQuadro[] }) {
  const { amostras, metodos, mapa, temPendente } = useMemo(() => {
    /* Onda 303 (RN-248): a coluna de cada amostra leva o nome que o
       cliente deu, com o código AM embaixo — continua ordenada pelo código,
       que é a ordem em que a Testesolo recebeu. */
    const am = new Map<string, { codigo: string; identificacao: string | null }>();
    const me = new Map<string, string>();
    const m = new Map<string, CelulaDoQuadro>();
    let pendente = false;
    for (const c of celulas) {
      am.set(c.amostra_id, { codigo: c.amostra_codigo, identificacao: c.amostra_identificacao });
      me.set(c.metodo_codigo, c.metodo_nome);
      m.set(`${c.metodo_codigo}|${c.amostra_id}`, c);
      if (Number(c.ensaios) > Number(c.com_laudo)) pendente = true;
    }
    return {
      amostras: [...am.entries()].sort((a, b) => a[1].codigo.localeCompare(b[1].codigo, 'pt-BR')),
      metodos: [...me.entries()].sort((a, b) => a[1].localeCompare(b[1], 'pt-BR')),
      mapa: m,
      temPendente: pendente,
    };
  }, [celulas]);

  /* Sem ensaio aprovado, a seção inteira não aparece — uma tabela vazia
     só ocuparia espaço e faria o cliente procurar o que não existe. */
  if (celulas.length === 0) return null;

  const somaColuna = (amostraId: string) => metodos.reduce(
    (t, [cod]) => t + Number(mapa.get(`${cod}|${amostraId}`)?.ensaios ?? 0), 0);
  const somaEnsaios = (metodo: string) => amostras.reduce(
    (t, [id]) => t + Number(mapa.get(`${metodo}|${id}`)?.ensaios ?? 0), 0);
  const somaCorpos = (metodo: string) => amostras.reduce(
    (t, [id]) => t + Number(mapa.get(`${metodo}|${id}`)?.corpos_de_prova ?? 0), 0);

  const totalEnsaios = metodos.reduce((t, [c]) => t + somaEnsaios(c), 0);
  const totalCorpos = metodos.reduce((t, [c]) => t + somaCorpos(c), 0);

  return (
    <>
      <div className="secao">
        <h2>Ensaios executados</h2>
        <span className="conta">{totalEnsaios} ensaio(s) aprovado(s)</span>
      </div>

      <p className="quadro-nota">
        O que já foi executado e aprovado nesta ordem de serviço, amostra
        por amostra. É o mesmo conjunto que entra no boletim de medição —
        use este quadro para conferir as quantidades do boletim.
      </p>

      <div className="rola">
        <table className="quadro">
          <thead>
            <tr>
              <th scope="col">Ensaio</th>
              {amostras.map(([id, a]) => (
                <th scope="col" key={id}><NomeDaAmostra amostra={a} /></th>
              ))}
              <th scope="col">Ensaios</th>
              <th scope="col">Corpos de prova</th>
            </tr>
          </thead>
          <tbody>
            {metodos.map(([cod, nome]) => (
              <tr key={cod}>
                <th scope="row">{nome}</th>
                {amostras.map(([id]) => {
                  const c = mapa.get(`${cod}|${id}`);
                  const n = Number(c?.ensaios ?? 0);
                  const aCaminho = c && n > Number(c.com_laudo);
                  return (
                    <td key={id} className="celula">
                      {n === 0 ? <span className="vazia">—</span> : (
                        <span className={aCaminho ? 'a_caminho' : undefined}
                              title={aCaminho ? 'Laudo a caminho' : undefined}>
                          {n}
                        </span>
                      )}
                    </td>
                  );
                })}
                <td className="celula total">{somaEnsaios(cod)}</td>
                <td className="celula total">
                  {somaCorpos(cod) || <span className="vazia">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Total</th>
              {amostras.map(([id]) => (
                <td key={id} className="celula">{somaColuna(id)}</td>
              ))}
              <td className="celula">{totalEnsaios}</td>
              <td className="celula">{totalCorpos || '—'}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {temPendente && (
        <p className="quadro-nota">
          <span className="a_caminho">Número sublinhado</span>: ensaio
          concluído e aprovado cujo laudo ainda não foi disponibilizado
          aqui. Ele já conta na medição, e o documento chega em seguida.
        </p>
      )}
    </>
  );
}

function Eixo({ titulo, opcoes, atual, aoEscolher, amostras }: {
  titulo: string;
  opcoes: Filtro[];
  atual: string | null;
  aoEscolher(v: string | null): void;
  /** Eixo de amostras: o nome do cliente e o código (onda 303, RN-248). */
  amostras?: boolean;
}) {
  if (opcoes.length === 0) return null;
  return (
    <div className="eixo">
      <div className="eixo-rotulo">{titulo}</div>
      <div className="filtros">
        {opcoes.map((o) => {
          const ligado = atual === o.chave;
          return (
            <button
              key={o.chave}
              type="button"
              className="ficha"
              /*
               * `aria-pressed` e não só uma classe: quem usa leitor de
               * tela ouve «pressionado», e quem não distingue oliva de
               * creme vê o ✓. A cor nunca é o único sinal (§18).
               */
              aria-pressed={ligado}
              onClick={() => aoEscolher(ligado ? null : o.chave)}
            >
              {ligado && <span aria-hidden="true">✓</span>}
              {amostras
                ? <NomeDaAmostra emLinha
                    amostra={{ codigo: o.rotulo, identificacao: o.identificacao }} />
                : <span>{o.rotulo}</span>}
              <span className="conta">{o.documentos}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Uma linha da `/cptu?os=` (a `vw_portal_cptu`, 0258). */
interface RelatorioInterativo {
  relatorio_id: string; numero: string; revisao: number; campanha_codigo: string | null; campanha_titulo: string | null;
  verticais: number; emitido_em: string | null;
}

/**
 * O relatório interativo CPTu da OS (onda 317, spec 07 §8): uma faixa só quando a OS tem um entregue. A lista vem
 * da `vw_portal_cptu` — a mesma cerca dos documentos — e o cartão leva ao interativo, que tem o PDF e a planilha.
 */
function RelatoriosInterativos({ osId }: { osId: string }) {
  const [lista, setLista] = useState<RelatorioInterativo[]>([]);
  useEffect(() => {
    const parar = new AbortController();
    api<RelatorioInterativo[]>(`/cptu?os=${encodeURIComponent(osId)}`, { sinal: parar.signal })
      .then(setLista)
      .catch(() => setLista([]));
    return () => parar.abort();
  }, [osId]);
  if (lista.length === 0) return null;
  return (
    <>
      <div className="secao"><h2>Relatório interativo CPTu</h2></div>
      <ul className="cptu-portal-lista">
        {lista.map((r) => (
          <li key={r.relatorio_id}>
            <Link className="cartao-os cptu-portal-cartao" to={`/cptu/${r.relatorio_id}`}>
              <b>{r.campanha_titulo ?? 'Campanha CPTu'}</b>
              <span className="sub">
                <span className="mono">{r.numero} · R{String(r.revisao).padStart(2, '0')}</span>
                {' · '}{r.verticais === 1 ? '1 vertical' : `${r.verticais} verticais`}
                {' · '}emitido em {dataBr(r.emitido_em)}
              </span>
              <span className="cptu-portal-cartao-acao">Abrir o relatório interativo — perfis, ábacos, mapa, corte e 3D</span>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}

function CartaoDoDocumento({ d, largo, aberto, aoAbrir }: {
  d: Documento; largo: boolean; aberto: boolean; aoAbrir(): void;
}) {
  const ver = enderecoDoDocumento(d.tipo, d.documento_id);
  const baixar = enderecoDoDocumento(d.tipo, d.documento_id, true);
  const revisao = `R${String(d.revisao).padStart(2, '0')}`;

  return (
    <li className="doc">
      <div className="doc-tipo" aria-hidden="true">PDF</div>
      <div className="doc-corpo">
        <h3>{d.titulo}</h3>
        <div className="metas">
          <span className="meta">
            <span className="rot">Documento</span>
            <span className="val mono">{d.numero} {revisao}</span>
          </span>
          {d.amostras_codigo.length > 0 && (
            <span className="meta">
              <span className="rot">
                {d.amostras_codigo.length > 1 ? 'Amostras' : 'Amostra'}
              </span>
              {/* Onda 303 (RN-248): pelo nome do cliente, código AM depois. */}
              <span className="val">{d.amostras_codigo.map((c, i) => nomeDaAmostra({
                codigo: c, identificacao: d.amostras_identificacao?.[i] ?? null,
              })).join(', ')}</span>
            </span>
          )}
          {tamanho(d.tamanho_bytes) && (
            <span className="meta">
              <span className="rot">Arquivo</span>
              <span className="val mono">{tamanho(d.tamanho_bytes)}</span>
            </span>
          )}
          <span className="meta">
            <span className="rot">Emitido</span>
            <span className="val mono">{dataHoraBr(d.emitido_em)}</span>
          </span>
        </div>

        <div className="acoes">
          {largo ? (
            <button type="button" className="botao" onClick={aoAbrir}>
              {aberto ? 'Fechar' : 'Ver na tela'}
            </button>
          ) : (
            /*
              No celular, aba nova: o leitor nativo do aparelho faz zoom,
              busca e rolagem melhor que qualquer coisa que esta página
              faria dentro de um quadro de 320 px.
            */
            <a className="botao" href={ver} target="_blank" rel="noreferrer">
              Abrir
            </a>
          )}
          <a className="botao secundario" href={baixar}>Baixar</a>
          {d.token_autenticidade && (
            <a
              className="vinculo-verificar"
              href={`/r/${d.token_autenticidade}`}
              target="_blank"
              rel="noreferrer"
            >
              Verificar autenticidade
            </a>
          )}
        </div>

        {aberto && largo && (
          <iframe
            style={{
              width: '100%', height: 'min(78vh, 900px)', marginTop: '.9rem',
              border: '1px solid var(--linha)', borderRadius: 'var(--raio)',
              background: 'var(--papel)',
            }}
            src={ver}
            title={`${d.titulo} — ${d.numero} ${revisao}`}
          />
        )}
      </div>
    </li>
  );
}
